import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { connect } from 'react-redux';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { DeepsightComponentLoader } from '@commons/DeepsightComponents';
import { getClientInfo } from '@selectors/client';
import { getTheme } from '@commons/utils/theme';

import { METRIC_TYPE } from '../common/utils';
import { METRIC_TYPE_OPTIONS } from './utils/config';
import ChartTooltip from '../common/ChartTooltip';
import RestaurantLimitedDropdown from '../common/RestaurantLimitedDropdown';

const DeliveryComparisonChart = ({
  selectedStores,
  tableData,
  isLoading,
  selectedMetric,
  isPercentage,
  client: { storeName },
}) => {
  const [selectedStoresForComparison, setSelectedStoresForComparison] = useState([]);
  const [chartData, setChartData] = useState([]);
  const theme = getTheme();

  // Prepare chart data for delivery only
  useEffect(() => {
    if (!tableData || !selectedStoresForComparison.length) {
      return;
    }

    const data = selectedStoresForComparison.map((store) => {
      const storeData = tableData.find((data) => data.id === store.id) || {};
      const propertyKey = isPercentage ? 'deliveryPercentage' : 'delivery';

      return {
        name: store.name,
        current: storeData[propertyKey]?.current,
        previous: storeData[propertyKey]?.previous,
      };
    });
    setChartData(data);
  }, [tableData, selectedStoresForComparison, isPercentage]);

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, height: 420, border: '1px solid #E0E0E0', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 420 }}>
          <DeepsightComponentLoader />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, height: 420, border: '1px solid #E0E0E0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {i18next.t('ANALYSIS.PERFORMANCE.DELIVERY_COMPARISON', { storeName })}
        </Typography>

        <RestaurantLimitedDropdown
          selectedStores={selectedStoresForComparison}
          setSelectedStores={setSelectedStoresForComparison}
          stores={selectedStores}
        />
      </Box>

      {chartData.length > 0 ? (
        <ResponsiveContainer height={300} width="100%">
          <BarChart data={chartData} margin={{ bottom: 5, left: 20, right: 30, top: 20 }}>
            <CartesianGrid stroke="#EDF0F6" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fill: '#6B768C', fontSize: 12 }} tickLine={false} />
            <YAxis
              axisLine={false}
              tick={{ fill: '#6B768C', fontSize: 12 }}
              tickFormatter={(value) => (isPercentage ? `${value.toFixed(0)}%` : value)}
              tickLine={false}
            />
            <Tooltip
              content={
                <ChartTooltip
                  isPercentage={isPercentage}
                  options={METRIC_TYPE_OPTIONS().map((option) => ({
                    id: option.id,
                    type: isPercentage ? METRIC_TYPE.PERCENTAGE : option.type,
                  }))}
                  selectedMetric={selectedMetric}
                />
              }
              cursor={{ fill: 'rgba(0, 0, 0, 0.03)' }}
            />
            <Legend />

            {/* Previous period bar (grey) */}
            <Bar
              dataKey="previous"
              fill={theme.colors.greys.medium}
              name={i18next.t('ANALYSIS.PERFORMANCE.PREVIOUS_PERIOD')}
              radius={[2, 2, 0, 0]}
            />

            {/* Current period bar (blue) */}
            <Bar
              dataKey="current"
              fill={theme.colors.blue}
              name={i18next.t('ANALYSIS.PERFORMANCE.CURRENT_PERIOD')}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <Typography color="textSecondary" variant="body2">
            {i18next.t('GENERAL.NO_DATA_TO_DISPLAY')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  client: getClientInfo(state.baseReducer.user),
});

export default connect(mapStateToProps, null)(DeliveryComparisonChart);
